import React, { useCallback, useMemo } from 'react';
import { Select } from 'antd';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

export interface IInsightSelectOption {
  value: string;
  label: string;
}

export type InsightSelectVariant = 'card' | 'filter';

export interface IInsightSelectProps {
  options: IInsightSelectOption[];
  /** The selected option's value; absent draws the placeholder. */
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shows antd's clear icon; clearing calls `onChange` with an empty string. */
  allowClear?: boolean;
  /**
   * `card` is the section-card chrome; `filter` is the filter panel's own palette. The two are
   * different token sets rather than different components — see design/filter-panel-spec §3.2.
   */
  variant?: InsightSelectVariant;
  /** CSS width. Every call site is a measured width, so there is no size scale to pick from. */
  width?: string;
  id?: string;
  ariaLabel?: string;
  /** The id of the element that describes the field, such as its error. */
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  className?: string;
}

// Figma draws the filter card's select field at 374x40 with a 9px radius and a 13px text inset
// (design/filter-panel-spec-2026-08-11.md § 3.2); none of those land on the token ramps.
const FILTER_SELECT_HEIGHT = '40px';
const FILTER_SELECT_RADIUS = '9px';
const FILTER_SELECT_INSET = '13px';

/*
 * The dropdown renders inside this host (see getPopupContainer), so antd's light defaults are
 * restyled from here — a body-level portal would also drop out of the --is-* token scope.
 */
const SelectHost = styled.div<{ $variant: InsightSelectVariant; $width?: string }>`
  position: relative;
  width: ${({ $width }) => $width || '100%'};

  .ant-select {
    width: 100%;
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    border: 1px solid ${tokens.color['border-subtle']};
    border-radius: ${tokens.radius.sm};
    background: ${tokens.color['surface-card-alt']};
    color: ${tokens.color['text-primary']};
    font-size: ${tokens.type['body-size']};
    line-height: ${tokens.type['body-line']};
  }

  .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
  .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
    border-color: ${tokens.color['border-strong']};
    box-shadow: none;
  }

  .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
    box-shadow: ${tokens.elevation['focus-ring']};
  }

  .ant-select-disabled:not(.ant-select-customize-input) .ant-select-selector {
    background: ${tokens.color['surface-subpanel']};
    color: ${tokens.color['text-muted']};
  }

  .ant-select-selection-placeholder {
    color: ${tokens.color['text-muted']};
  }

  .ant-select-arrow,
  .ant-select-clear {
    color: ${tokens.color['text-muted']};
  }

  .ant-select-clear {
    background: transparent;
  }

  .ant-select-clear:hover {
    color: ${tokens.color['text-secondary']};
  }

  .ant-select-dropdown {
    border-radius: ${tokens.radius.sm};
    background: ${tokens.color['surface-card']};
    box-shadow: ${tokens.elevation.overlay};
  }

  .ant-select-item-option {
    color: ${tokens.color['text-secondary']};
    font-size: ${tokens.type['body-size']};
    line-height: ${tokens.type['body-line']};
  }

  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background: ${tokens.color['surface-card-alt']};
    color: ${tokens.color['text-primary']};
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: ${tokens.color['border-subtle']};
    color: ${tokens.color['text-primary']};
    font-weight: ${tokens.type['label-weight']};
  }

  ${({ $variant }) =>
    $variant === 'filter' &&
    `
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
      height: ${FILTER_SELECT_HEIGHT};
      padding: 0 ${FILTER_SELECT_INSET};
      border-color: ${tokens.color['filter-accent']};
      border-radius: ${FILTER_SELECT_RADIUS};
      background: ${tokens.color['filter-surface']};
      color: ${tokens.color['filter-text']};
      font-weight: ${tokens.type['card-title-weight']};
    }

    .ant-select-single .ant-select-selector .ant-select-selection-item,
    .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
      line-height: ${FILTER_SELECT_HEIGHT};
    }

    .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
    .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
      border-color: ${tokens.color['filter-accent']};
    }

    .ant-select-arrow {
      color: ${tokens.color['filter-accent']};
    }

    .ant-select-dropdown {
      background: ${tokens.color['filter-surface']};
      border: 1px solid ${tokens.color['filter-border']};
    }

    .ant-select-item-option {
      color: ${tokens.color['filter-text-muted']};
    }

    .ant-select-item-option-active:not(.ant-select-item-option-disabled),
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
      background: ${tokens.color['filter-border']};
      color: ${tokens.color['filter-text']};
    }
  `}
`;

// The one single-select control of the Intelligence suite: the Product Readiness picker, every
// filter card's header field and the Demographics sub-selects are the same antd Select behind the
// same restyled chrome. PLA-8815.
const InsightSelect: React.FC<IInsightSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  allowClear = false,
  variant = 'card',
  width,
  id,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  className
}) => {
  const selectOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : []).map(({ value: id_, label }) => ({
        value: id_,
        label
      })),
    [options]
  );

  // antd calls back with `(value, option)`; the extra argument is swallowed here so a caller's
  // handler can never be fed antd's option object as a second parameter.
  const handleChange = useCallback((next?: string) => onChange(next ?? ''), [onChange]);

  // Nothing to choose between: the caller draws its own empty treatment rather than an inert control.
  if (selectOptions.length === 0) {
    return null;
  }

  return (
    <SelectHost
      className={className}
      $variant={variant}
      $width={width}
      data-testid="insight-select"
    >
      <Select<string>
        id={id}
        value={value}
        options={selectOptions}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        onChange={handleChange}
        getPopupContainer={triggerNode => triggerNode.parentElement}
      />
    </SelectHost>
  );
};

export default InsightSelect;
